/**
 * The stacking-overflow indicator for one Platform lane (UI spec §4.3, R10.15,
 * task 22.3).
 *
 * R10.15 caps the simultaneously rendered stacking rows per grouping and requires
 * an indicator that states how many Initiatives of the grouping are *not*
 * rendered, plus an expand action that reveals the rest **without changing any
 * applied filter**. This is that indicator: it states the unrendered count and
 * toggles the lane between its capped and fully expanded layouts. The cap is a
 * rendering budget, not a filter — expanding a dense lane never touches the
 * filter state, so the result count and every other lane are unaffected.
 *
 * The indicator is a real `<button>` with an accessible name, positioned at the
 * lane's right edge where the clipped rows would continue. Colour is not the only
 * carrier: the count and the action verb are text.
 */
export interface LaneOverflowIndicatorProps {
  /** Initiatives of this lane the cap kept off screen (R10.15). */
  readonly unrenderedCount: number;
  /** Whether the lane is currently expanded past its cap. */
  readonly expanded: boolean;
  /** Toggles the lane between capped and expanded; changes no applied filter. */
  readonly onToggle: () => void;
}

import { useTranslation } from 'react-i18next';

export function LaneOverflowIndicator({
  unrenderedCount,
  expanded,
  onToggle,
}: LaneOverflowIndicatorProps): React.JSX.Element {
  const { t } = useTranslation();
  const label = expanded
    ? t('laneOverflow.collapse')
    : t('laneOverflow.expand', { count: unrenderedCount });
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={expanded}
      aria-label={
        expanded
          ? t('laneOverflow.collapseAria')
          : t('laneOverflow.expandAria', { count: unrenderedCount })
      }
      className="border-line bg-surface-alt text-accent-hover hover:bg-accent-soft absolute bottom-[3px] right-[3px] z-10 rounded-[var(--radius-badge)] border px-[6px] py-[2px] text-[10px] font-semibold outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--accent)]"
    >
      {expanded ? '▲ ' : '▾ '}
      {label}
    </button>
  );
}
